import { useRouter } from 'vue-router';
import type { RouteLocationRaw } from 'vue-router';
import { getHostRouter } from '@Src/mfe.config';
import { refreshUserIdentity } from 'host/mfeBridge';
import { useAuthFlowStore } from '@Stores/authFlow.store';
import { getNextAuthFlowApi } from '@Api/authFlow.api';
import { resolveDeviceAuthEnabled } from './useDeviceAuthFlag';
import { SessionExpiredError, redirectToSessionExpired } from '@Utils/sessionExpired';

const useFlowNavigation = () => {
    const router = useRouter();
    const hostRouter = getHostRouter();
    const authFlowStore = useAuthFlowStore();

    /**
     * When device auth is enabled, checks the backend for additional auth
     * flows (e.g. RDS after OTP). 'handled' means new flows were found and
     * navigation happened; 'none' means the backend genuinely returned no
     * further steps; 'fetch-failed' means the flow fetch itself failed, so
     * nothing is known about remaining steps. An expired session also counts
     * as 'handled': the user is sent to the session-expired screen.
     */
    const checkForAdditionalFlows = async (
        navigate: (to: RouteLocationRaw) => void
    ): Promise<'handled' | 'none' | 'fetch-failed'> => {
        const deviceAuthEnabled = await resolveDeviceAuthEnabled();
        if (!deviceAuthEnabled) return 'none';

        let newRoutes: Awaited<ReturnType<typeof getNextAuthFlowApi>>;
        try {
            newRoutes = await getNextAuthFlowApi();
        } catch (error) {
            if (error instanceof SessionExpiredError) {
                redirectToSessionExpired();
                return 'handled';
            }
            throw error;
        }

        // The API returns undefined only when the request itself failed —
        // that is not the same as "no steps remain".
        if (newRoutes === undefined) return 'fetch-failed';
        if (newRoutes.length === 0) return 'none';

        authFlowStore._clearFlowSequence();
        authFlowStore.addToAuthFlow(newRoutes);

        if (authFlowStore.hasUnrecognizedOnly) {
            // A step-up was requested but every step name was unrecognized —
            // stop rather than silently continuing the main flow (PLAT-2990).
            navigate({ name: 'general-error' });
            return 'handled';
        }

        const currentQuery = { ...router.currentRoute.value.query };
        const nextTask = authFlowStore.getNextFlowTask();

        if (nextTask) {
            navigate({ name: nextTask.routeName, query: currentQuery });
            return 'handled';
        }

        return 'none';
    };

    /**
     * Advances to the next flow step, fetching additional flows from the
     * backend when the local sequence is exhausted. By default a failed flow
     * fetch behaves like "no steps remain" and the main flow continues; a
     * caller gating on the evaluation (e.g. a fraud step-up) can pass
     * haltOnFetchFailure to stay put instead, since an outage must not read
     * as clearance.
     */
    const advanceFlow = async (
        options?: { haltOnFetchFailure?: boolean }
    ): Promise<'navigated' | 'continued' | 'fetch-failed'> => {
        const currentQuery = { ...router.currentRoute.value.query };
        const nextTask = authFlowStore.getNextFlowTask();

        if (nextTask) {
            router.push({ name: nextTask.routeName, query: currentQuery });
            return 'navigated';
        }

        const result = await checkForAdditionalFlows((to) => router.push(to));
        if (result === 'handled') {
            return 'navigated';
        }
        if (result === 'fetch-failed' && options?.haltOnFetchFailure) {
            return 'fetch-failed';
        }
        continueMainFlow();
        return 'continued';
    };

    const goBackInFlow = () => {
        const currentQuery = router.currentRoute.value.query;
        const prevTask = authFlowStore.getPreviousFlowTask();

        if (prevTask) {
            router.push({ name: prevTask.routeName, query: currentQuery });
        }
    };

    const advanceFlowReplace = async () => {
        const currentQuery = { ...router.currentRoute.value.query };
        const nextTask = authFlowStore.getNextFlowTask();

        if (nextTask) {
            await router.replace({ name: nextTask.routeName, query: currentQuery });
            return;
        }

        const result = await checkForAdditionalFlows((to) => router.replace(to));
        if (result !== 'handled') {
            continueMainFlow();
        }
    };

    const continueMainFlow = () => {
        const companySlug = hostRouter.currentRoute.value.params.companySlug;

        console.log('[continueMainFlow] companySlug:', companySlug);
        console.log('[continueMainFlow] hostRoute:', hostRouter.currentRoute.value.fullPath);

        // if continuing main flow, assume auth is complete so request identity refresh
        refreshUserIdentity();
        hostRouter.push({
            name: 'continue-flow',
            params: { companySlug }
        });
    };

    return {
        advanceFlow,
        advanceFlowReplace,
        continueMainFlow,
        goBackInFlow
    };
};

export default useFlowNavigation;
